// src/Blog.js
import React, { useState, useEffect } from 'react';
import axiosInstance from './axiosInstance';
import { Link } from 'react-router-dom';
import './Blog.css'; // Import the CSS file for the Blog page

const Blog = () => {
  const [isMobile, setIsMobile] = useState(false);
  const [wordOfTheDay, setWordOfTheDay] = useState(null);
  const [loadingWord, setLoadingWord] = useState(true);
  const [errorWord, setErrorWord] = useState('');
  const [blogPosts, setBlogPosts] = useState([]);
  const [loadingBlogs, setLoadingBlogs] = useState(true);
  const [errorBlogs, setErrorBlogs] = useState('');
  const [likeMessages, setLikeMessages] = useState({});
  const [comments, setComments] = useState({});
  const [commentText, setCommentText] = useState({});
  const [commentMessages, setCommentMessages] = useState({});
  const [replies, setReplies] = useState({});
  const [replyText, setReplyText] = useState({});
  const [replyMessages, setReplyMessages] = useState({});
  const [showReplyInput, setShowReplyInput] = useState({});

  useEffect(() => {
    const handleResize = () => {
      if (window.innerWidth <= 768) {
        setIsMobile(true); // For mobile screens
      } else {
        setIsMobile(false); // For larger screens
      }
    };

    window.addEventListener('resize', handleResize);
    handleResize(); // Run once to check initial window size

    return () => window.removeEventListener('resize', handleResize);
  }, []);

  useEffect(() => {
    const fetchWordOfTheDay = async () => {
      try {
        const response = await axiosInstance.get('/word-of-day/current');
        setWordOfTheDay(response.data);
      } catch (err) {
        console.error(err);
        setErrorWord('Failed to fetch the current Word of the Day');
      } finally {
        setLoadingWord(false);
      }
    };

    fetchWordOfTheDay();
  }, []);

  useEffect(() => {
    const fetchBlogPosts = async () => {
      try {
        const response = await axiosInstance.get('/blog');
        setBlogPosts(response.data);
      } catch (err) {
        console.error(err);
        setErrorBlogs('Failed to fetch blogs');
      } finally {
        setLoadingBlogs(false);
      }
    };

    fetchBlogPosts();
  }, []);

  const handleLikeWordOfTheDay = async (id) => {
    try {
      const response = await axiosInstance.post(`/word-of-day/${id}/like`);
      if (response.status === 200) {
        setWordOfTheDay((prev) => ({ ...prev, likes: response.data.likes }));
        setLikeMessages((prev) => ({ ...prev, wordOfTheDay: response.data.message }));
      }
    } catch (err) {
      console.error('Failed to like/unlike Word of the Day:', err);
      if (err.response && err.response.status === 400) {
        setLikeMessages((prev) => ({ ...prev, wordOfTheDay: 'Please login to like' }));
      } else if (err.response && err.response.data && err.response.data.name === 'TokenExpiredError') {
        setLikeMessages((prev) => ({ ...prev, wordOfTheDay: 'Please login' }));
      } else {
        setLikeMessages((prev) => ({ ...prev, wordOfTheDay: 'Failed to like/unlike' }));
      }
    }
  };

  const handleLikeBlogPost = async (id) => {
    try {
      const response = await axiosInstance.post(`/blog/${id}/like`);
      if (response.status === 200) {
        setBlogPosts((prev) =>
          prev.map((post) =>
            post.id === id ? { ...post, likes: response.data.likes } : post
          )
        );
        setLikeMessages((prev) => ({ ...prev, [id]: response.data.message }));
      }
    } catch (err) {
      console.error('Failed to like/unlike blog post:', err);
      if (err.response && err.response.status === 400) {
        setLikeMessages((prev) => ({ ...prev, [id]: 'Please login to like' }));
      } else if (err.response && err.response.data && err.response.data.name === 'TokenExpiredError') {
        setLikeMessages((prev) => ({ ...prev, [id]: 'Please login' }));
      } else {
        setLikeMessages((prev) => ({ ...prev, [id]: 'Failed to like/unlike' }));
      }
    }
  };

  const handleCommentChange = (e, id) => {
    setCommentText({ ...commentText, [id]: e.target.value });
  };

  const handleCommentSubmit = async (e, id, type) => {
    e.preventDefault();
    const endpoint = type === 'blog' ? `/blog/${id}/comment` : `/word-of-day/${id}/comment`;
    try {
      const response = await axiosInstance.post(endpoint, { text: commentText[id] });
      if (response.status === 201) {
        setCommentMessages((prev) => ({ ...prev, [id]: 'Comment added successfully' }));
        setCommentText({ ...commentText, [id]: '' });
        // Re-fetch comments to update the UI
        fetchComments(id, type);
        // Update comments count
        if (type === 'wordOfTheDay') {
          setWordOfTheDay((prev) => ({ ...prev, comments: prev.comments + 1 }));
        } else {
          setBlogPosts((prev) =>
            prev.map((post) =>
              post.id === id ? { ...post, comments: post.comments + 1 } : post
            )
          );
        }
      }
    } catch (err) {
      console.error('Failed to add comment:', err);
      if (err.response) {
        if (err.response.status === 401 || (err.response.data && err.response.data.name === 'TokenExpiredError')) {
          setCommentMessages((prev) => ({ ...prev, [id]: 'Please login to add a comment' }));
        } else {
          setCommentMessages((prev) => ({ ...prev, [id]: 'Failed to add comment' }));
        }
      } else {
        setCommentMessages((prev) => ({ ...prev, [id]: 'Failed to add comment' }));
      }
    }
  };

  const handleReplyChange = (e, commentId) => {
    setReplyText({ ...replyText, [commentId]: e.target.value });
  };

  const handleReplySubmit = async (e, commentId, type) => {
    e.preventDefault();
    const endpoint = type === 'wordOfTheDay' ? `/word-of-day/comment/${commentId}/reply` : `/blog/comment/${commentId}/reply`;
    try {
      const response = await axiosInstance.post(endpoint, { text: replyText[commentId] });
      if (response.status === 201) {
        setReplyMessages((prev) => ({ ...prev, [commentId]: 'Reply added successfully' }));
        setReplyText({ ...replyText, [commentId]: '' });
        // Re-fetch replies to update the UI
        fetchReplies(commentId, type);
      }
    } catch (err) {
      console.error('Failed to add reply:', err);
      if (err.response) {
        if (err.response.status === 401 || (err.response.data && err.response.data.name === 'TokenExpiredError')) {
          setReplyMessages((prev) => ({ ...prev, [commentId]: 'Please login to add a reply' }));
        } else {
          setReplyMessages((prev) => ({ ...prev, [commentId]: 'Failed to add reply' }));
        }
      } else {
        setReplyMessages((prev) => ({ ...prev, [commentId]: 'Failed to add reply' }));
      }
    }
  };

  const fetchComments = async (id, type) => {
    const endpoint = type === 'wordOfTheDay' ? `/word-of-day/${id}/comments` : `/blog/${id}/comments`;
    try {
      const response = await axiosInstance.get(endpoint);
      setComments((prev) => ({ ...prev, [id]: response.data }));
    } catch (err) {
      console.error('Failed to fetch comments:', err);
    }
  };

  const fetchReplies = async (commentId, type) => {
    const endpoint = type === 'wordOfTheDay' ? `/word-of-day/comment/${commentId}/replies` : `/blog/comment/${commentId}/replies`;
    try {
      const response = await axiosInstance.get(endpoint);
      setReplies((prev) => ({ ...prev, [commentId]: response.data }));
    } catch (err) {
      console.error('Failed to fetch replies:', err);
    }
  };

  const toggleReplyInput = (commentId, type) => {
    setShowReplyInput((prev) => ({ ...prev, [commentId]: !prev[commentId] }));
    if (!showReplyInput[commentId]) {
      fetchReplies(commentId, type);
    }
  };

  useEffect(() => {
    if (wordOfTheDay) {
      fetchComments(wordOfTheDay.id, 'wordOfTheDay');
    }
  }, [wordOfTheDay]);

  useEffect(() => {
    blogPosts.forEach((post) => {
      fetchComments(post.id, 'blog');
    });
  }, [blogPosts]);

  const gridStyle = {
    display: 'grid',
    gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr', // 1 column for mobile, 2 columns for larger screens
    gap: '16px', // Space between the cards
  };

  const cardStyle = {
    padding: '16px',
    borderRadius: '8px',
    backgroundColor: '#2d2d2d',
    color: 'white',
    boxShadow: '0 4px 8px rgba(0, 0, 0, 0.2)',
    transition: 'transform 0.3s, box-shadow 0.3s, border-color 0.3s',
    border: '2px solid #ff7c0a',
  };

  const titleStyle = {
    fontSize: '1.5rem',
    fontWeight: 'bold',
    marginTop: '16px',
    color: '#ff7c0a',
  };

  const subtitleStyle = {
    fontSize: '1.25rem',
    fontWeight: 'bold',
    marginTop: '8px',
    color: '#ff7c0a',
  };

  const bodyStyle = {
    fontSize: '1rem',
    marginTop: '8px',
    color: 'white',
  };

  const linkStyle = {
    marginTop: '16px',
    color: '#ff7c0a',
    fontWeight: 'bold',
    textDecoration: 'none',
  };

  const buttonStyle = {
    backgroundColor: '#ff7c0a',
    color: 'white',
    padding: '12px 24px',
    borderRadius: '8px',
    fontSize: '1rem',
    fontWeight: 'bold',
    textDecoration: 'none',
    transition: 'background-color 0.3s',
  };

  const iconStyle = {
    marginRight: '8px',
    cursor: 'pointer',
  };

  const messageStyle = {
    color: '#ff7c0a',
    marginTop: '8px',
  };

  const commentStyle = {
    marginTop: '16px',
    textAlign: 'left',
  };

  const commentFormStyle = {
    display: 'flex',
    flexDirection: 'column',
    marginTop: '16px',
  };

  const commentInputStyle = {
    padding: '8px',
    borderRadius: '4px',
    border: '1px solid #ddd',
    marginBottom: '8px',
  };

  const commentButtonStyle = {
    ...buttonStyle,
    alignSelf: 'flex-start',
  };

  const replyStyle = {
    marginLeft: '16px',
    marginTop: '8px',
  };

  const replyFormStyle = {
    display: 'flex',
    flexDirection: 'column',
    marginTop: '8px',
  };

  const replyInputStyle = {
    padding: '8px',
    borderRadius: '4px',
    border: '1px solid #ddd',
    marginBottom: '8px',
  };

  const replyButtonStyle = {
    ...buttonStyle,
    alignSelf: 'flex-start',
  };

  const imageStyle = {
    width: '100%',
    height: '300px',
    objectFit: 'cover',
    borderRadius: '8px',
  };

  // const imgStyle = {
  //   width: '100%',
  //   height: '200px', // Adjust for the image size
  //   objectFit: 'cover',
  //   borderRadius: '8px',
  // };

  return (
    <section
      style={{
        padding: '32px 16px',
        backgroundColor: 'black',
        color: 'white',
        textAlign: 'center',
        margin: '0 16px', // Add margin left and right
      }}
    >
      {loadingWord ? (
        <p>Loading Word of the Day...</p>
      ) : errorWord ? (
        <p>{errorWord}</p>
      ) : wordOfTheDay ? (
        <div style={{ marginBottom: '32px', display: 'flex', justifyContent: 'center' }}>
          <div style={{ ...cardStyle, width: isMobile ? '100%' : '60%' }}>
            <h3 style={titleStyle}>{wordOfTheDay.title}</h3>
            {wordOfTheDay.subtitle && <h4 style={subtitleStyle}>{wordOfTheDay.subtitle}</h4>}
            <p style={bodyStyle}>{wordOfTheDay.body}</p>
            <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: '16px' }}>
              <div style={{ display: 'flex', alignItems: 'center' }}>
                <span style={iconStyle} onClick={() => handleLikeWordOfTheDay(wordOfTheDay.id)}>👍</span>
                <span>{wordOfTheDay.likes}</span>
              </div>
              <div style={{ display: 'flex', alignItems: 'center' }}>
                <span style={iconStyle}>💬</span>
                <span>{wordOfTheDay.comments}</span>
              </div>
            </div>
            {likeMessages.wordOfTheDay && <p style={messageStyle}>{likeMessages.wordOfTheDay}</p>}
            <div style={commentStyle}>
              <h4>Comments</h4>
              {Array.isArray(comments[wordOfTheDay.id]) && comments[wordOfTheDay.id].map((comment, index) => (
                <div key={index}>
                  <p><strong>{comment.User?.first_name}:</strong> {comment.text}</p>
                  <button onClick={() => toggleReplyInput(comment.id, 'wordOfTheDay')} className="reply-text">Replies</button>
                  {showReplyInput[comment.id] && (
                    <div style={replyStyle}>
                      <h5>Replies</h5>
                      {Array.isArray(replies[comment.id]) && replies[comment.id].map((reply, index) => (
                        <p key={index}><strong>{reply.User?.first_name}:</strong> {reply.text}</p>
                      ))}
                      <form style={replyFormStyle} onSubmit={(e) => handleReplySubmit(e, comment.id, 'wordOfTheDay')}>
                        <input
                          type="text"
                          placeholder="Add a reply"
                          value={replyText[comment.id] || ''}
                          onChange={(e) => handleReplyChange(e, comment.id)}
                          style={replyInputStyle}
                        />
                        <button type="submit" style={replyButtonStyle}>Reply</button>
                      </form>
                      {replyMessages[comment.id] && <p style={messageStyle}>{replyMessages[comment.id]}</p>}
                    </div>
                  )}
                </div>
              ))}
              <form style={commentFormStyle} onSubmit={(e) => handleCommentSubmit(e, wordOfTheDay.id, 'wordOfTheDay')}>
                <input
                  type="text"
                  placeholder="Add a comment"
                  value={commentText[wordOfTheDay.id] || ''}
                  onChange={(e) => handleCommentChange(e, wordOfTheDay.id)}
                  style={commentInputStyle}
                />
                <button type="submit" style={commentButtonStyle}>Comment</button>
              </form>
              {commentMessages[wordOfTheDay.id] && <p style={messageStyle}>{commentMessages[wordOfTheDay.id]}</p>}
            </div>
          </div>
        </div>
      ) : (
        <p>No Word of the Day found</p>
      )}
            <h2 style={{ fontSize: '2.5rem', fontWeight: 'bold', marginBottom: '32px' }}>
        Blog
      </h2>
      {loadingBlogs ? (
        <p>Loading Blogs...</p>
      ) : errorBlogs ? (
        <p>{errorBlogs}</p>
      ) : (
        <div style={gridStyle}>
          {blogPosts.map((post) => (
            <div key={post.id} style={cardStyle}>
              <img src={post.image} alt={post.title} style={imageStyle} />
              <h3 style={titleStyle}>{post.title}</h3>
              {post.subtitle && <h4 style={subtitleStyle}>{post.subtitle}</h4>}
              <p style={bodyStyle}>{post.body.slice(0, 70)}...</p>
              <Link to={`/blog/${post.id}`} style={linkStyle}>
                Read More
              </Link>
              <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: '16px' }}>
                <div style={{ display: 'flex', alignItems: 'center' }}>
                  <span style={iconStyle} onClick={() => handleLikeBlogPost(post.id)}>👍 {post.likes}</span>
                </div>
                <div style={{ display: 'flex', alignItems: 'center' }}>
                  <Link to={`/blog/${post.id}`} style={linkStyle}>
                    <span style={iconStyle}>💬 {post.comments}</span>
                  </Link>
                </div>
              </div>
              {likeMessages[post.id] && <p style={messageStyle}>{likeMessages[post.id]}</p>}
            </div>
          ))}
        </div>
      )}
    </section>
  );
};

export default Blog;